import React, {FC, useCallback, useEffect, useState} from 'react'
import {Link, useHistory, useLocation, useParams} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {fullName, TRACKING_STATUS_BADGE, trackingStatusLabel} from '../application/ApplicationModel'
import {ApplicationDetailModal} from '../application/ApplicationDetailModal'
import {formatDateTime} from '../shared/DocumentApi'
import {CvActions} from '../shared/CvActions'
import {TagChips} from '../tag/TagChips'
import {ITagTarget, removeCandidateTag, TagAssignModal} from '../tag/TagAssignModal'
import {ITagModel} from '../tag/TagModel'
import {getCandidate, updateCandidate} from './Api'
import {HR_CANDIDATE_PATH, ICandidateModel, ICandidateUpdateRequest} from './CandidateModel'
import {CandidateEditModal} from './CandidateEditModal'
import {CallResultPanel} from '../call-pool/CallResultPanel'
import {CALL_ENTRY_PARAM, HR_CALL_POOL_PATH} from '../call-pool/CallPoolModel'
import {CandidateInterviews} from '../interview/CandidateInterviews'

const initials = (candidate: ICandidateModel) =>
  `${candidate.firstname?.charAt(0) ?? ''}${candidate.lastname?.charAt(0) ?? ''}`.toUpperCase()

/** Aday künyesi: kişisel bilgiler, güncel CV, notlar ve tüm başvuruları */
const CandidateDetailPage: FC = () => {
  const {id} = useParams<{id: string}>()
  const history = useHistory()
  const location = useLocation()
  // Arama havuzundan açıldıysa künyede Olumlu / Olumsuz gösterilir
  const callEntryId = new URLSearchParams(location.search).get(CALL_ENTRY_PARAM)
  const [candidate, setCandidate] = useState<ICandidateModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [applicationId, setApplicationId] = useState<string | null>(null)

  const fetchCandidate = useCallback(() => {
    setLoading(true)
    getCandidate(id)
      .then(({data}) => setCandidate(data.payload))
      .catch((error) => showErrorToast(apiErrorMessage(error, 'Aday bulunamadı')))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    fetchCandidate()
  }, [fetchCandidate])

  const handleUpdate = async (request: ICandidateUpdateRequest) => {
    setSaving(true)
    try {
      await updateCandidate(id, request)
      showSuccessToast('Aday güncellendi')
      setEditing(false)
      fetchCandidate()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Aday güncellenemedi'))
    } finally {
      setSaving(false)
    }
  }

  const [tagTarget, setTagTarget] = useState<ITagTarget | null>(null)

  const handleRemoveTag = async (tag: ITagModel) => {
    if (!candidate) return
    try {
      await removeCandidateTag(
        {candidateId: candidate.id, candidateName: fullName(candidate), tags: candidate.tags || []},
        tag
      )
      fetchCandidate()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiket kaldırılamadı'))
    }
  }

  if (loading && !candidate) {
    return (
      <div className='d-flex justify-content-center py-20'>
        <span className='spinner-border text-primary' />
      </div>
    )
  }

  if (!candidate) {
    return (
      <div className='card'>
        <div className='card-body text-center py-20'>
          <div className='fs-4 fw-bold mb-4'>Aday bulunamadı</div>
          <Link to={HR_CANDIDATE_PATH} className='btn btn-light'>
            Aday Havuzuna Dön
          </Link>
        </div>
      </div>
    )
  }

  const infoItems: {icon: string; label: string; value?: string | null}[] = [
    {icon: 'bi-envelope', label: 'E-posta', value: candidate.email},
    {icon: 'bi-telephone', label: 'Telefon', value: candidate.phone},
    {icon: 'bi-geo-alt', label: 'Yaşadığı yer', value: candidate.candidateLocation},
    {icon: 'bi-calendar-check', label: 'Son başvuru', value: formatDateTime(candidate.lastApplicationDate)},
    {icon: 'bi-person-plus', label: 'Havuza eklenme', value: formatDateTime(candidate.createdDate)},
  ]

  return (
    <>
      <PageTitle
        breadcrumbs={[
          callEntryId
            ? {title: 'Arama Havuzu', path: HR_CALL_POOL_PATH, isActive: false}
            : {title: 'Aday Havuzu', path: HR_CANDIDATE_PATH, isActive: false},
        ]}
      >
        Aday Künyesi
      </PageTitle>

      {callEntryId && (
        <CallResultPanel
          entryId={callEntryId}
          onDone={(message) => {
            showSuccessToast(message)
            // Aday arama listesinden düştü; künye arama kaydıyla birlikte yenilenir
            history.replace(location.pathname)
            fetchCandidate()
          }}
        />
      )}

      <div className='row g-6'>
        <div className='col-xl-4'>
          <div className='card h-100'>
            <div className='card-body pt-10 d-flex flex-column align-items-center'>
              <div className='symbol symbol-100px symbol-circle mb-5'>
                <span className='symbol-label fs-1 fw-bolder bg-light-primary text-primary'>{initials(candidate)}</span>
              </div>
              <div className='fs-2 fw-bolder text-gray-900 mb-1 text-center'>{fullName(candidate)}</div>
              <div className='text-muted mb-3'>{candidate.applicationCount} başvuru</div>

              <div className='d-flex flex-wrap justify-content-center align-items-center gap-1 mb-6'>
                <TagChips tags={candidate.tags} onRemove={handleRemoveTag} />
                <button
                  type='button'
                  className='btn btn-sm btn-link text-warning p-0 ms-2 mt-1'
                  onClick={() =>
                    setTagTarget({candidateId: candidate.id, candidateName: fullName(candidate), tags: candidate.tags || []})
                  }
                >
                  <i className='bi bi-tags text-warning'></i>
                  Etiketle
                </button>
              </div>

              <div className='d-flex flex-wrap justify-content-center gap-2 mb-8'>
                <CvActions documentId={candidate.documentId} fileName={`${fullName(candidate)} CV`} />
                <button type='button' className='btn btn-sm btn-light-primary' onClick={() => setEditing(true)}>
                  <i className='bi bi-pencil'></i>
                  Düzenle
                </button>
              </div>

              <div className='w-100'>
                {infoItems.map((item) => (
                  <div key={item.label} className='d-flex align-items-center py-3 border-bottom border-gray-200'>
                    <i className={`bi ${item.icon} fs-4 text-gray-500 me-4`}></i>
                    <div className='d-flex flex-column'>
                      <span className='text-muted fs-8'>{item.label}</span>
                      <span className='fw-semibold text-gray-800'>{item.value || '-'}</span>
                    </div>
                  </div>
                ))}
              </div>

              {candidate.note && (
                <div className='w-100 mt-6'>
                  <div className='text-muted fs-8 mb-1'>İK Notu</div>
                  <div className='bg-light rounded p-4' style={{whiteSpace: 'pre-line'}}>
                    {candidate.note}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-xl-8 d-flex flex-column gap-6'>
          <div className='card'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bolder'>Başvuruları</h3>
            </div>
            <div className='card-body pt-0'>
              {(candidate.applications || []).length === 0 ? (
                <div className='text-muted py-10 text-center'>Bu adaya bağlı başvuru yok.</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed gy-4'>
                    <thead>
                      <tr className='text-muted fw-bolder fs-7 text-uppercase'>
                        <th>İlan</th>
                        <th>Başvuru Tarihi</th>
                        <th>Süreç</th>
                        <th className='text-end'></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(candidate.applications || []).map((application) => (
                        <tr key={application.id || ''}>
                          <td>
                            <div className='fw-bold'>{application.jobTitle}</div>
                            <div className='text-muted fs-7'>{application.hrProcessName}</div>
                          </td>
                          <td className='fs-7'>
                            {formatDateTime(application.createdDate)}
                            {application.source === 'CALL_POOL' && (
                              <div>
                                <span className='badge badge-light-info mt-1'>Aramadan</span>
                              </div>
                            )}
                          </td>
                          <td>
                            {application.trackingStatus ? (
                              <div className='d-flex flex-column gap-1'>
                                <span
                                  className={`badge ${TRACKING_STATUS_BADGE[application.trackingStatus]} align-self-start`}
                                >
                                  {trackingStatusLabel(application.trackingStatus)}
                                </span>
                                <span className='text-muted fs-7'>{application.currentStepName}</span>
                              </div>
                            ) : application.outcome === 'NEGATIVE' ? (
                              <span className='badge badge-light-danger'>Olumsuz</span>
                            ) : (
                              <span className='badge badge-light'>Başlatılmadı</span>
                            )}
                          </td>
                          <td className='text-end'>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-info'
                              onClick={() => setApplicationId(application.id || null)}
                            >
                              <i className='bi bi-eye'></i>
                              Detay
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className='card'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bolder'>Arama Kayıtları</h3>
            </div>
            <div className='card-body pt-0'>
              {(candidate.callLogs || []).length === 0 ? (
                <div className='text-muted py-6 text-center'>Bu aday henüz aranmadı.</div>
              ) : (
                (candidate.callLogs || []).map((log) => (
                  <div key={log.id} className='d-flex gap-4 py-4 border-bottom border-gray-200'>
                    <i
                      className={`bi ${
                        log.result === 'POSITIVE' ? 'bi-hand-thumbs-up text-success' : 'bi-hand-thumbs-down text-danger'
                      } fs-2`}
                    ></i>
                    <div className='d-flex flex-column flex-grow-1'>
                      <div className='d-flex flex-wrap align-items-center gap-2'>
                        <span className={`badge ${log.result === 'POSITIVE' ? 'badge-light-success' : 'badge-light-danger'}`}>
                          {log.result === 'POSITIVE' ? 'Olumlu' : 'Olumsuz'}
                        </span>
                        {log.negativeReasonLabel && <span className='fw-semibold'>{log.negativeReasonLabel}</span>}
                        <span className='text-muted fs-7 ms-auto'>
                          {formatDateTime(log.createdDate)}
                          {log.calledByUserName ? ` · ${log.calledByUserName}` : ''}
                        </span>
                      </div>
                      {log.startedJobs && (
                        <div className='fs-7 mt-2'>
                          <span className='text-muted'>Süreç başlatılan ilanlar: </span>
                          {log.startedJobs}
                        </div>
                      )}
                      {log.salaryExpectation && (
                        <div className='fs-7'>
                          <span className='text-muted'>Ücret beklentisi: </span>
                          {log.salaryExpectation}
                        </div>
                      )}
                      {log.note && (
                        <div className='fs-7 text-gray-700 mt-1' style={{whiteSpace: 'pre-line'}}>
                          {log.note}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <CandidateInterviews candidateId={id} />
        </div>
      </div>

      <CandidateEditModal
        candidate={editing ? candidate : null}
        loading={saving}
        onClose={() => setEditing(false)}
        onSubmit={handleUpdate}
      />
      <ApplicationDetailModal applicationId={applicationId} onClose={() => setApplicationId(null)} />
      <TagAssignModal target={tagTarget} onClose={() => setTagTarget(null)} onSaved={fetchCandidate} />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {CandidateDetailPage}
